// Hooks
import {useState, useMemo, useEffect} from 'react'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useHistory} from 'react-router-dom'
import {useToast} from '@/hooks/useToast'
import {useIntl} from 'react-intl'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useLearningCenter from '~/app/hooks/use-learning-center'
import {
    useFetchParentLevel,
    useFetchParentGoal,
    useLearningCenterMutation,
    useFetchModule,
    useFetchModulePageUrl
} from 'hec/hooks/learning-center'
import {
    useCheckEnrollment,
    useCheckAvailableToEarnPoints,
    useAwardPoints
} from 'hec/hooks/annex-cloud/useAnnexCloud'
import useLearningCenterGuest from './useLearningCenterGuest'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getLoyaltyEnrollment} from 'hec/helpers/getLoyaltyEnrollment'

// Helpers
import {
    areAllModulesComplete,
    areAllLevelsComplete
} from 'hec/builder/helpers/learningCenterHelpers'
import {getNextIncompleteModuleID} from 'hec/builder/helpers/learningCenterHelpers'
import {updateAcLoyaltyDataOnServer} from '~/app/utils/helpers'

// Models
import {createModule} from 'hec/builder/models/learning-center'

// Constants
import {ctaTypes, quizStatuses} from './lcModuleConstants'

// Other
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {isPreviewing} from '@builder.io/sdk-react'
import {scroller} from 'react-scroll'
import {isClient} from '@/utils/common'

const useLearningCenterModule = (builderModule) => {
    const {guestLCData, updateGuestLCData} = useLearningCenterGuest()
    const isBuilderEditorPreviewing = isPreviewing()
    const config = getConfig()
    const history = useHistory()
    const {
        data: customer,
        isLoading: customerIsLoading,
        isError: customerIsError,
        error: customerError
    } = useCurrentCustomer()

    const {isRegistered, customerId, customerType, c_acLoyaltyData} = customer
    const isGuest = customerType === 'guest'
    const {site} = useMultiSite()

    const {hasData: hasAcLoyaltyData} = useMemo(
        () => getLoyaltyEnrollment(c_acLoyaltyData, site.id),
        [c_acLoyaltyData, site.id]
    )
    const {data: moduleData, isLoading: isModuleDataLoading} = useFetchModule(builderModule.id)
    const {
        learningCenterData: sfccLearningCenterData,
        getModuleByID,
        getLevelByID,
        getGoalByID
    } = useLearningCenter()

    const {completeLesson, updateQuiz, startModule, setActiveLesson} = useLearningCenterMutation()
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    let module = null
    if (!isBuilderEditorPreviewing && moduleData) {
        const moduleID = moduleData.data?.id
        const sfccModule = getModuleByID(moduleID)
        module = createModule(moduleData, sfccModule)
    }

    const [selectedAccordionItem, setSelectedAccordionItem] = useState(() => {
        if (typeof window !== 'undefined') {
            const hash = window?.location?.hash?.slice(1)
            return hash || null
        }
        return null
    })

    const [quizStatus, setQuizStatus] = useState(quizStatuses.OPEN)
    const [requestInProgress, setRequestInProgress] = useState(false)

    const isLevelComplete = useMemo(() => {
        const levelCompletion = sfccLearningCenterData?.goals?.some((goal) =>
            goal?.levels?.some((level) => Boolean(level?.complete))
        )
        return levelCompletion
    }, [sfccLearningCenterData?.goals])

    const activeAccordionItem = useMemo(() => {
        if (selectedAccordionItem !== null) {
            return selectedAccordionItem
        }

        if (!module) return null

        if (module.allLessonsComplete) {
            return module.quiz.id
        } else {
            return module.firstIncompleteLessonID
        }
    }, [module, selectedAccordionItem])
    /*
        It's different from the selectedAccordionItem state because the selectedAccordionItem is null on the first load and it's set only when user navigates through the completed lessons or completes a new lesson. So the activeAccordionItem is required in order to set the active accordion tab on the first page load before user's interaction.
    */
    /*
        currentQuizStatus is used instead of the quizStatus state because module is null before the sfccLearningCenterData is set and we need to update the quiz status once the module is set. By default the quizStatus has to be OPEN (show the quiz form). But once the module is set and it's complete the quiz status becomes PASSED no matter what.
    */
    const currentQuizStatus = useMemo(
        () => (module?.complete ? quizStatuses.PASSED : quizStatus),
        [module?.complete, quizStatus]
    )

    const {
        data: parentLevelData,
        isLoading: parentLevelIsLoading,
        isError: parentLevelIsError,
        error: parentLevelError
    } = useFetchParentLevel({
        moduleContentId: module?.contentID,
        apiKey: config.app.builder.api,
        shouldQuery: true
    })

    const {
        data: parentGoalData,
        isLoading: parentGoalIsLoading,
        isError: parentGoalIsError,
        error: parentGoalError
    } = useFetchParentGoal({
        levelContentId: parentLevelData?.id,
        apiKey: config.app.builder.api,
        shouldQuery: true
    })

    const {data: enrollmentData} = useCheckEnrollment(customer?.email, site.id, {
        enabled: hasAcLoyaltyData
    })
    const isEnrolled = enrollmentData?.data?.optInStatus === 'YES'

    const {data: availablePointsData} = useCheckAvailableToEarnPoints(customer?.email, site.id, {
        enabled: isEnrolled
    })
    const updateAwardPoints = useAwardPoints()
    const toast = useToast()
    const {formatMessage} = useIntl()

    const ctaType = useMemo(() => {
        if (!module?.complete) return null

        const sfccLevel = getLevelByID(parentLevelData?.data?.id)
        const levelModules = parentLevelData?.data?.modules
        const allModulesComplete = levelModules
            ? areAllModulesComplete(levelModules, sfccLevel?.modules)
            : false
        const goalLevels = parentGoalData?.data.levels
        let allLevelsComplete = false

        if (allModulesComplete && goalLevels) {
            const sfccGoal = getGoalByID(parentGoalData?.data?.id)
            allLevelsComplete = areAllLevelsComplete(goalLevels, sfccGoal?.levels)
        }

        if (allLevelsComplete) {
            return ctaTypes.NEXT_GOAL
        } else if (allModulesComplete) {
            return ctaTypes.RETURN_TO_GOAL
        } else {
            return ctaTypes.NEXT_MODULE
        }
    }, [
        module?.complete,
        getGoalByID,
        getLevelByID,
        parentGoalData?.data?.id,
        parentGoalData?.data.levels,
        parentLevelData?.data?.id,
        parentLevelData?.data?.modules
    ])

    const nextModuleId = useMemo(() => {
        if (!parentLevelData) return null

        if (ctaType === ctaTypes.NEXT_MODULE) {
            const parentLevel = parentLevelData.data
            const sfccLevel = getLevelByID(parentLevel.id)
            const nextModuleID = getNextIncompleteModuleID(parentLevel, sfccLevel)
            return nextModuleID
        }

        return null
    }, [ctaType, getLevelByID, parentLevelData])

    const {modulePageUrl: nextModulePageUrl} = useFetchModulePageUrl({
        moduleId: nextModuleId,
        apiKey: config.app.builder.api
    })
    const nextModuleData = useMemo(() => {
        const data = {
            id: null,
            url: ''
        }

        switch (ctaType) {
            case ctaTypes.NEXT_MODULE:
                {
                    data.id = nextModuleId
                    data.url = nextModulePageUrl
                }
                break
            case ctaTypes.RETURN_TO_GOAL:
                {
                    const goalDashboardPageUrl = parentGoalData?.data?.dashboard?.value?.data?.url
                    data.url = goalDashboardPageUrl
                }
                break
            case ctaTypes.NEXT_GOAL:
                data.url = '/learning-center'
                break
        }

        return data
    }, [
        nextModulePageUrl,
        parentGoalData?.data?.dashboard?.value?.data?.url,
        ctaType,
        nextModuleId
    ])

    let isError = false

    if (customerIsError) {
        isError = true
        console.error('Failed to fetch current customer: ', customerError)
    }

    if (parentLevelIsError) {
        isError = true
        console.error('Failed to fetch parent level: ', parentLevelError)
    }

    if (parentGoalIsError) {
        isError = true
        console.error('Failed to fetch parent goal: ', parentGoalError)
    }

    const componentIsLoading =
        !isBuilderEditorPreviewing &&
        (isModuleDataLoading || parentLevelIsLoading || parentGoalIsLoading)
    const redirectToLearningCenter =
        !isBuilderEditorPreviewing && customerType !== null && !isRegistered && isClient

    const submitLesson = async (lesson, nextLesson, opts = {}) => {
        const {markOnly = false} = opts
        if (markOnly) {
            await completeLesson(lesson.id, nextLesson?.id ?? null, module.id, updateGuestLCData)
            return
        }
        if (!lesson.complete) {
            setRequestInProgress(true)
            const response = await completeLesson(
                lesson.id,
                nextLesson?.id ?? null,
                module.id,
                updateGuestLCData
            )
            if (!response.success) {
                throw new Error(response.errorMsg)
            }
            if (!markOnly) {
                setSelectedAccordionItem(nextLesson ? nextLesson.id : module.quiz.id)
            }

            setRequestInProgress(false)
        } else {
            if (!markOnly) {
                setSelectedAccordionItem(nextLesson ? nextLesson.id : module.quiz.id)
            }
        }
        if (!nextLesson) {
            setActiveLesson(window.location.pathname + '#' + module.quiz.id)
        }
    }

    const submitQuiz = async (data) => {
        setRequestInProgress(true)
        let quizPassed = true
        const {id: quizID, questions} = module.quiz

        for (let i = 0; i < questions.length; i++) {
            const correctAnswerIndex = questions[i].correctAnswerIndex

            if (data[`question-${i}`] !== correctAnswerIndex.toString()) {
                quizPassed = false
                break
            }
        }

        const {isLevelComplete, responseData} = await updateQuiz(
            quizPassed,
            quizID,
            module.id,
            parentLevelData?.data,
            parentGoalData?.data
        )

        if (responseData.success) {
            setQuizStatus(quizPassed ? quizStatuses.PASSED : quizStatuses.FAILED)
        } else {
            throw new Error(responseData.errorMsg)
        }

        setRequestInProgress(false)

        if (responseData.success && isLevelComplete && isEnrolled && availablePointsData) {
            updateAwardPoints.mutate(
                {
                    emailId: customer?.email,
                    siteId: site.id,
                    groupId: config.app.annexcloud.ANNEXCLOUD_GROUPS.LEARNING_CENTER,
                    actionId: parentLevelData?.data?.loyaltyActionId,
                    availablePointsData: availablePointsData.data
                },
                {
                    onSuccess: (data) => {
                        const updatedAvailablePoints = data?.data?.updatedAvailablePoints || 0
                        const updatedLifetimePoints = data?.data?.updatedLifetimePoints || 0
                        if (updatedAvailablePoints > 0) {
                            const currentLoyaltyData = JSON.parse(c_acLoyaltyData)
                            const pointsData = currentLoyaltyData.HSASTORE
                            if (pointsData.availablePoints >= 0) {
                                pointsData.availablePoints = updatedAvailablePoints
                            }
                            if (pointsData.lifetimePoints >= 0) {
                                pointsData.lifetimePoints = updatedLifetimePoints
                            }

                            updateAcLoyaltyDataOnServer(
                                customerId,
                                currentLoyaltyData,
                                updateCustomerMutation,
                                toast,
                                formatMessage
                            ).catch((error) => {
                                console.error('Failed to update AC loyalty data on server: ', error)
                            })
                        }
                    }
                }
            )
        }
    }

    const goToNextModule = async ({id, url}) => {
        if (ctaType === ctaTypes.NEXT_MODULE) {
            setRequestInProgress(true)
            const parentLevel = parentLevelData.data
            const response = await startModule(id, parentLevel.id, null, url)

            if (response.success) {
                history.push(url)
            } else {
                throw new Error(response.errorMsg)
            }
            setRequestInProgress(false)
        } else {
            setActiveLesson(url)
            history.push(url)
        }
    }

    return {
        module,
        requestInProgress,
        setRequestInProgress,
        componentIsLoading,
        activeAccordionItem,
        setSelectedAccordionItem,
        quizStatus: currentQuizStatus,
        setQuizStatus,
        ctaType,
        parentLevel: parentLevelData?.data,
        parentGoal: parentGoalData?.data,
        redirectToLearningCenter,
        isError,
        submitLesson,
        submitQuiz,
        goToNextModule,
        nextModuleData,
        isGuest,
        isLevelComplete,
        setActiveLesson
    }
}

export default useLearningCenterModule
